import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assests/images/logoone.png';
import { navigateWithLoader } from '../../components/navigation/ScrollToTop';
import CtaButton from '../../components/common/CtaButton';

/* ═══════════════════════════════════════════════════════════════
   useScrollReveal — exact copy of Home, About, Services & Industries hook
   Uses .sipl-reveal-* classes + IntersectionObserver (index.css)
   ═══════════════════════════════════════════════════════════════ */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );
    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

/* ═══════════════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════════════ */

const SOLUTION_INDEX_LIST = [
  { num: '01', title: 'ERP Solutions', targetId: 'sol-erp', accent: '#1a2f6e' },
  { num: '02', title: 'HRM Solutions', targetId: 'sol-hrm', accent: '#4caf50' },
  { num: '03', title: 'CRM Solutions', targetId: 'sol-crm', accent: '#0d9488' },
  { num: '04', title: 'LMS Solutions', targetId: 'sol-lms', accent: '#f97316' },
  { num: '05', title: 'Custom Business Applications', targetId: 'sol-custom', accent: '#1a2f6e' },
];

const SELECTOR_ITEMS = [
  {
    problem: 'Need to connect core business functions and centralize data across departments?',
    solution: 'ERP Solutions',
    targetId: 'sol-erp',
    accent: '#1a2f6e',
    borderClass: 'sipl-border-left-navy',
  },
  {
    problem: 'Need to manage employees, profiles, attendance, leave, and workforce processes?',
    solution: 'HRM Solutions',
    targetId: 'sol-hrm',
    accent: '#4caf50',
    borderClass: 'sipl-border-left-green',
  },
  {
    problem: 'Need to organize customer interactions, requests, follow-ups, and service activities?',
    solution: 'CRM Solutions',
    targetId: 'sol-crm',
    accent: '#0d9488',
    borderClass: 'sipl-border-left-teal',
  },
  {
    problem: 'Need to manage learning content, training programs, assessments, and progress?',
    solution: 'LMS Solutions',
    targetId: 'sol-lms',
    accent: '#f97316',
    borderClass: 'sipl-border-left-orange',
  },
  {
    problem: 'Need software designed around a unique operational workflow or portal?',
    solution: 'Custom Business Applications',
    targetId: 'sol-custom',
    accent: '#1a2f6e',
    borderClass: 'sipl-border-left-navy',
  },
];

const APPROACH_STEPS = [
  { num: '01', title: 'Understand', accentColor: '#1a2f6e', desc: 'We learn about the business, users, roles, workflows, and requirements.' },
  { num: '02', title: 'Map', accentColor: '#4caf50', desc: 'We document how information and decisions move through the process.' },
  { num: '03', title: 'Design', accentColor: '#0d9488', desc: 'We create the appropriate workflows, interfaces, permissions, and system structure.' },
  { num: '04', title: 'Build', accentColor: '#f97316', desc: 'We develop, integrate, test, and refine the solution.' },
  { num: '05', title: 'Deploy & Improve', accentColor: '#1a2f6e', desc: 'We deploy the system and continue improving it as business requirements evolve.' },
];

/* ═══════════════════════════════════════════════════════════════
   HERO VISUAL
   Refined SVG: PEOPLE - CUSTOMERS - OPERATIONS -> CENTRAL SYSTEM -> REPORTING
   ═══════════════════════════════════════════════════════════════ */
const SolutionsHeroVisual = () => (
  <div
    style={{ position: 'relative', width: '100%', maxWidth: 380, height: 360, margin: '0 auto' }}
    aria-hidden="true"
  >
    {/* Radial background glow */}
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      width: 290, height: 290, borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(26,47,110,0.07) 0%, rgba(76,175,80,0.04) 55%, transparent 70%)',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
    }} />

    {/* SVG composition */}
    <svg
      width="100%" height="100%"
      viewBox="0 0 380 360"
      fill="none"
      style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
    >
      {/* Dashed outer orbit ring */}
      <ellipse cx="190" cy="180" rx="155" ry="148" stroke="#cbd5e1" strokeWidth="1.2" strokeDasharray="5 7" />

      {/* 3 Top Input Nodes: PEOPLE (Left), CUSTOMERS (Center), OPERATIONS (Right) */}
      <line x1="75" y1="50" x2="190" y2="180" stroke="#4caf50" strokeWidth="1.5" strokeOpacity="0.35" />
      <line x1="190" y1="40" x2="190" y2="180" stroke="#0d9488" strokeWidth="1.5" strokeOpacity="0.35" />
      <line x1="305" y1="50" x2="190" y2="180" stroke="#1a2f6e" strokeWidth="1.5" strokeOpacity="0.35" />

      {/* Bottom Output Node: REPORTING */}
      <line x1="190" y1="180" x2="190" y2="320" stroke="#f97316" strokeWidth="1.5" strokeOpacity="0.35" />

      {/* Top 3 Circles */}
      <circle cx="75" cy="50" r="7" fill="#4caf50" />
      <circle cx="190" cy="40" r="7" fill="#0d9488" />
      <circle cx="305" cy="50" r="7" fill="#1a2f6e" />
      <circle cx="190" cy="320" r="7" fill="#f97316" />

      {/* Circle Halos */}
      <circle cx="75" cy="50" r="12" stroke="#4caf50" strokeWidth="1" strokeOpacity="0.25" fill="none" />
      <circle cx="190" cy="40" r="12" stroke="#0d9488" strokeWidth="1" strokeOpacity="0.25" fill="none" />
      <circle cx="305" cy="50" r="12" stroke="#1a2f6e" strokeWidth="1" strokeOpacity="0.25" fill="none" />
      <circle cx="190" cy="320" r="12" stroke="#f97316" strokeWidth="1" strokeOpacity="0.25" fill="none" />

      {/* Node Labels */}
      <text x="75" y="30" textAnchor="middle" fontSize="9" fontWeight="800" fill="#4caf50" fontFamily="Plus Jakarta Sans, system-ui" letterSpacing="0.08em">PEOPLE</text>
      <text x="190" y="22" textAnchor="middle" fontSize="9" fontWeight="800" fill="#0d9488" fontFamily="Plus Jakarta Sans, system-ui" letterSpacing="0.08em">CUSTOMERS</text>
      <text x="305" y="30" textAnchor="middle" fontSize="9" fontWeight="800" fill="#1a2f6e" fontFamily="Plus Jakarta Sans, system-ui" letterSpacing="0.08em">OPERATIONS</text>
      <text x="190" y="342" textAnchor="middle" fontSize="9" fontWeight="800" fill="#f97316" fontFamily="Plus Jakarta Sans, system-ui" letterSpacing="0.08em">REPORTING &amp; INSIGHTS</text>
    </svg>

    {/* Central Logo Hub (CENTRAL SYSTEM) */}
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 112, height: 112, borderRadius: '50%',
      padding: 4,
      background: 'linear-gradient(135deg, #1a2f6e, #0d9488, #4caf50, #f97316)',
      boxShadow: '0 12px 32px rgba(26,47,110,0.14), 0 4px 12px rgba(0,0,0,0.04)',
      zIndex: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: '100%', height: '100%', borderRadius: '50%',
        background: '#ffffff', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 8,
      }}>
        <img src={logo} alt="Seikural Innovation" loading="lazy" decoding="async" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 1 — HERO SECTION
   ═══════════════════════════════════════════════════════════════ */
const SolutionsHero = () => {
  return (
    <section
      aria-labelledby="solutions-hero-heading"
      className="sipl-hero"
      style={{
        position: 'relative',
        background: '#ffffff',
        overflow: 'hidden',
        paddingTop: '2rem',
        paddingBottom: '1.75rem',
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      <style>{`
        @keyframes sipl-sol-slide-ltr {
          from { opacity: 0; transform: translateX(-28px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes sipl-sol-fadein {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sipl-sol-nav-row {
          transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), background-color 0.2s ease, border-color 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        .sipl-sol-nav-row:hover {
          transform: translateX(4px);
          background-color: #f8fafc;
        }
        .sipl-sol-nav-row:hover .sipl-sol-row-title {
          color: var(--row-accent, #1a2f6e) !important;
        }
        .sipl-sol-nav-row:hover .sipl-sol-row-arrow {
          transform: translateX(5px);
          color: var(--row-accent, #1a2f6e) !important;
        }
        .sipl-sol-row-arrow {
          transition: transform 0.22s ease, color 0.2s ease;
        }
      `}</style>

      {/* Subtle bg blobs */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '-10%', right: '-5%', width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26,47,110,0.05) 0%, transparent 65%)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-15%', left: '-8%', width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(76,175,80,0.05) 0%, transparent 65%)'
        }} />
      </div>

      <div className="container" style={{ position: 'relative', zIndex: 5 }}>
        <div className="row align-items-center g-4">

          {/* Left Column — Content */}
          <div className="col-lg-6">

            {/* Eyebrow */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 50, padding: '3px 12px', marginBottom: 10,
              animation: 'sipl-sol-slide-ltr 0.8s cubic-bezier(0.16,1,0.3,1) both',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4caf50', display: 'inline-block' }} />
              <span style={{ color: '#1a2f6e', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                BUSINESS SOLUTIONS
              </span>
            </div>

            {/* H1 */}
            <h1
              id="solutions-hero-heading"
              style={{
                fontSize: 'clamp(1.9rem, 3.2vw, 2.7rem)',
                fontWeight: 900, lineHeight: 1.18,
                letterSpacing: '-0.03em', color: '#0f172a',
                marginBottom: 14,
                animation: 'sipl-sol-slide-ltr 0.9s cubic-bezier(0.16,1,0.3,1) 0.05s both',
              }}
            >
              Business Software Built<br />
              <span style={{ color: '#1a2f6e' }}>Around the Way You Work.</span>
            </h1>

            {/* Supporting Text */}
            <p style={{
              fontSize: '0.92rem', color: '#64748b', lineHeight: 1.75,
              maxWidth: 490, marginBottom: 22,
              animation: 'sipl-sol-fadein 0.8s ease 0.2s both',
            }}>
              From enterprise resource planning and employee management to customer relationships, learning, and custom operational workflows, Seikural builds business solutions around the processes that matter to your organization.
            </p>

            {/* CTAs */}
            <div
              className="d-flex flex-wrap align-items-center gap-3"
              style={{ animation: 'sipl-sol-fadein 0.8s ease 0.35s both' }}
            >
              <CtaButton to="/contact" variant="primary">
                Discuss Your Requirement
              </CtaButton>
              <CtaButton to="/services" variant="secondary">
                Explore Services
              </CtaButton>
            </div>

          </div>

          {/* Right Column — Visual */}
          <div
            className="col-12 col-lg-6 text-center"
            style={{ animation: 'sipl-sol-fadein 0.9s ease 0.15s both' }}
          >
            <SolutionsHeroVisual />
          </div>

        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SECTION 2 — SOLUTION OVERVIEW & INDEX NAVIGATION
   ═══════════════════════════════════════════════════════════════ */
const SolutionsOverview = () => {
  const handleScrollTo = (targetId) => {
    navigateWithLoader(targetId);
  };

  return (
    <section id="solution-overview" className="py-5 bg-light" aria-label="Solutions Overview">
      <div className="container py-4">
        <div className="row g-5 align-items-start">

          {/* Left Column — Editorial Text */}
          <div className="col-12 col-lg-5 sipl-reveal-ltr">
            <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
              WHAT WE SOLVE
            </span>
            <h2 className="display-6 fw-bold text-dark mb-4">
              Business systems designed around real workflows.
            </h2>
            <p className="text-muted mb-3" style={{ lineHeight: '1.8', fontSize: '0.95rem' }}>
              Businesses often work across multiple teams, processes, spreadsheets, applications, and sources of information.
            </p>
            <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem' }}>
              The right business software can bring those workflows together, reduce repetitive work, improve visibility, and give teams a more consistent way to manage operations.
            </p>
          </div>

          {/* Right Column — Solution Navigation Index */}
          <div className="col-12 col-lg-6 offset-lg-1 sipl-reveal-rtl">
            <p className="text-uppercase fw-bold text-muted mb-3" style={{ letterSpacing: '0.1em', fontSize: '0.7rem' }}>
              Core Business Solutions
            </p>
            <div className="card border-0 shadow-sm rounded-4 p-2 p-md-3 bg-white">
              {SOLUTION_INDEX_LIST.map((item, i) => (
                <div
                  key={item.num}
                  onClick={() => handleScrollTo(item.targetId)}
                  className="sipl-sol-nav-row p-3 rounded-3 d-flex align-items-center justify-content-between"
                  style={{
                    cursor: 'pointer',
                    '--row-accent': item.accent,
                    borderBottom: i < SOLUTION_INDEX_LIST.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <span className="fw-bold text-muted" style={{ fontSize: '0.8rem', minWidth: 26 }}>
                      {item.num}
                    </span>
                    <span
                      className="rounded-circle flex-shrink-0"
                      style={{ width: 8, height: 8, background: item.accent, display: 'inline-block' }}
                    />
                    <span className="sipl-sol-row-title fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                      {item.title}
                    </span>
                  </div>
                  <span className="sipl-sol-row-arrow fw-bold text-muted" style={{ fontSize: '1.1rem' }}>
                    →
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SECTION 3 — SOLUTION 01: ERP SOLUTIONS
   ═══════════════════════════════════════════════════════════════ */
const SolutionERP = () => (
  <section id="sol-erp" className="py-5 bg-white border-top" aria-label="ERP Solutions">
    <div className="container py-4">

      {/* Header */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ltr">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge sipl-bg-navy text-white px-2.5 py-1 rounded fw-bold" style={{ fontSize: '0.75rem' }}>
              01 / ERP
            </span>
          </div>
          <h2 className="display-6 fw-bold text-dark mb-3">
            ERP Solutions
          </h2>
          <p className="fw-bold sipl-text-navy fs-5 mb-3">
            Connect core business operations through one structured system.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem', maxWidth: 740 }}>
            ERP solutions bring multiple business functions into a connected environment so teams can work with centralized information, shared workflows, and consistent processes.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-start">

        {/* Left Column — What it manages & Capabilities */}
        <div className="col-12 col-lg-7 sipl-reveal-ltr">

          {/* Core Areas */}
          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 sipl-border-left-navy">
            <h3 className="h6 fw-bold text-dark mb-3">What an ERP system can manage</h3>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {[
                'Sales Workflows',
                'Purchasing & Procurement',
                'Inventory Management',
                'Financial Records',
                'Vendor Management',
                'Customer Records',
                'Operations',
                'Reporting & Dashboards',
                'Approval Workflows',
                'Document Repository',
              ].map((item) => (
                <span key={item} className="badge bg-light text-dark border px-3 py-2 rounded-pill font-weight-medium" style={{ fontSize: '0.78rem', color: '#1e293b', borderColor: '#e2e8f0' }}>
                  <span style={{ color: '#1a2f6e', marginRight: 5 }}>•</span> {item}
                </span>
              ))}
            </div>
          </div>

          {/* Core Capabilities */}
          <h3 className="h6 text-uppercase fw-bold text-muted mb-3" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            Key System Capabilities
          </h3>
          <div className="row g-3">
            {[
              { title: 'Centralized Data', desc: 'Single source of truth for operational and business information.' },
              { title: 'Role-Based Access', desc: 'Secure permissions tailored to user roles and management levels.' },
              { title: 'Approval Workflows', desc: 'Automate multi-stage approvals for purchase orders, expenses, and documents.' },
              { title: 'Reports & Dashboards', desc: 'Real-time financial, inventory, and sales performance visibility.' },
            ].map((cap) => (
              <div key={cap.title} className="col-12 col-md-6">
                <div className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white sipl-card-hover sipl-border-left-navy">
                  <h4 className="h6 fw-bold text-dark mb-1">{cap.title}</h4>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Right Column — Suitable For, Value & CTA */}
        <div className="col-12 col-lg-5 sipl-reveal-rtl">

          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-light">
            <h3 className="h6 fw-bold text-dark mb-3">Who ERP is suitable for</h3>
            <div className="d-flex flex-column gap-2 mb-3">
              {[
                'Growing businesses scaling operations',
                'Companies managing multiple operational functions',
                'Organizations struggling with disconnected tools',
                'Teams relying heavily on manual spreadsheets',
              ].map((fit) => (
                <div key={fit} className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>→</span>
                  <span className="text-muted small mb-0">{fit}</span>
                </div>
              ))}
            </div>

            <p className="text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
              Business Outcomes
            </p>
            <div className="d-flex flex-column gap-1.5">
              {[
                'Reduce disconnected processes',
                'Improve operational visibility',
                'Centralize business information',
                'Standardize internal workflows',
                'Support clearer reporting',
              ].map((out) => (
                <div key={out} className="d-flex align-items-center gap-2">
                  <span className="rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: 16, height: 16, background: '#1a2f6e', fontSize: '0.6rem', flexShrink: 0 }}>✓</span>
                  <span className="fw-semibold text-dark small">{out}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="p-4 rounded-4 text-white" style={{ background: 'linear-gradient(135deg, #1a2f6e 0%, #0d9488 100%)' }}>
            <h4 className="h6 fw-bold text-white mb-2">Need a tailored ERP solution?</h4>
            <p className="text-light small mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Let's evaluate your operational workflows and design a structured business system.
            </p>
            <Link to="/contact" className="btn btn-light text-dark fw-bold rounded-3 px-4 py-2 small d-inline-block shadow-sm">
              Discuss an ERP Requirement →
            </Link>
          </div>

        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 4 — SOLUTION 02: HRM SOLUTIONS
   ═══════════════════════════════════════════════════════════════ */
const SolutionHRM = () => (
  <section id="sol-hrm" className="py-5 bg-light border-top" aria-label="HRM Solutions">
    <div className="container py-4">

      {/* Header */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ltr">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge sipl-bg-green text-white px-2.5 py-1 rounded fw-bold" style={{ fontSize: '0.75rem' }}>
              02 / HRM
            </span>
          </div>
          <h2 className="display-6 fw-bold text-dark mb-3">
            HRM Solutions
          </h2>
          <p className="fw-bold sipl-text-green fs-5 mb-3">
            Bring employee and workforce processes into one place.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem', maxWidth: 740 }}>
            HRM solutions help organizations manage employee information and recurring workforce processes through structured digital workflows.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-start">

        {/* Left Column — Suitable for & Benefits */}
        <div className="col-12 col-lg-5 sipl-reveal-ltr">

          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white sipl-border-left-green">
            <h3 className="h6 fw-bold text-dark mb-3">Who HRM is suitable for</h3>
            <div className="d-flex flex-column gap-2 mb-3">
              {[
                'Growing teams needing structured HR records',
                'Organizations managing workforce processes manually',
                'Businesses requiring centralized employee data',
                'Companies looking to automate attendance & leave',
              ].map((fit) => (
                <div key={fit} className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>→</span>
                  <span className="text-muted small mb-0">{fit}</span>
                </div>
              ))}
            </div>

            <p className="text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
              Business Value
            </p>
            <div className="d-flex flex-column gap-1.5">
              {[
                'Centralize employee information',
                'Reduce manual HR administration',
                'Improve workflow visibility',
                'Standardize employee processes',
                'Support better HR reporting',
              ].map((val) => (
                <div key={val} className="d-flex align-items-center gap-2">
                  <span className="rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: 16, height: 16, background: '#4caf50', fontSize: '0.6rem', flexShrink: 0 }}>✓</span>
                  <span className="fw-semibold text-dark small">{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="p-4 rounded-4 text-white" style={{ background: 'linear-gradient(135deg, #4caf50 0%, #0d9488 100%)' }}>
            <h4 className="h6 fw-bold text-white mb-2">Looking to streamline your HR workflows?</h4>
            <p className="text-light small mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Tell us about your team size and workforce management requirements.
            </p>
            <Link to="/contact" className="btn btn-light text-dark fw-bold rounded-3 px-4 py-2 small d-inline-block shadow-sm">
              Discuss an HRM Requirement →
            </Link>
          </div>

        </div>

        {/* Right Column — Modules & Areas */}
        <div className="col-12 col-lg-7 sipl-reveal-rtl">
          <h3 className="h6 text-uppercase fw-bold text-muted mb-4" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            HRM Functional Modules
          </h3>
          <div className="row g-3">
            {[
              { title: 'Employee Profiles', desc: 'Centralized directory for personnel details, contracts, and documents.' },
              { title: 'Attendance & Tracking', desc: 'Log work hours, shifts, attendance, and remote activity records.' },
              { title: 'Leave Management', desc: 'Automate leave requests, approvals, balances, and calendar tracking.' },
              { title: 'Payroll & Payslips', desc: 'Manage salary structures, deductions, payslip generation, and disbursements.' },
              { title: 'Departments & Roles', desc: 'Define organizational hierarchy, teams, roles, and reporting managers.' },
              { title: 'HR Analytics & Reports', desc: 'Generate headcount, attendance, turnover, and workforce performance insights.' },
            ].map((mod) => (
              <div key={mod.title} className="col-12 col-md-6">
                <div className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white sipl-card-hover sipl-border-left-green">
                  <h4 className="h6 fw-bold text-dark mb-1">{mod.title}</h4>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{mod.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 5 — SOLUTION 03: CRM SOLUTIONS
   ═══════════════════════════════════════════════════════════════ */
const SolutionCRM = () => (
  <section id="sol-crm" className="py-5 bg-white border-top" aria-label="CRM Solutions">
    <div className="container py-4">

      {/* Header */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ltr">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge sipl-bg-teal text-white px-2.5 py-1 rounded fw-bold" style={{ fontSize: '0.75rem' }}>
              03 / CRM
            </span>
          </div>
          <h2 className="display-6 fw-bold text-dark mb-3">
            CRM Solutions
          </h2>
          <p className="fw-bold sipl-text-teal fs-5 mb-3">
            Keep customer relationships, requests, and activities connected.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem', maxWidth: 740 }}>
            CRM solutions help businesses organize customer information, interactions, requests, follow-ups, and service activities in a centralized system.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-start">

        {/* Left Column — CRM Workflow & Areas */}
        <div className="col-12 col-lg-7 sipl-reveal-ltr">
          <h3 className="h6 text-uppercase fw-bold text-muted mb-4" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            CRM Capabilities & Features
          </h3>
          <div className="row g-3 mb-4">
            {[
              { title: 'Customer Records', desc: 'Centralize contact details, company profiles, and account histories.' },
              { title: 'Lead & Request Tracking', desc: 'Capture customer inquiries and track leads from initial contact to conversion.' },
              { title: 'Follow-up & Task Assignment', desc: 'Assign sales/service tasks, set reminders, and log interaction history.' },
              { title: 'Service Activity History', desc: 'Keep track of customer requests, tickets, communications, and status.' },
            ].map((crm) => (
              <div key={crm.title} className="col-12 col-md-6">
                <div className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white sipl-card-hover sipl-border-left-teal">
                  <h4 className="h6 fw-bold text-dark mb-1">{crm.title}</h4>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{crm.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CRM Flow Visual */}
          <div className="card border-0 shadow-sm rounded-4 p-4 bg-light">
            <h4 className="h6 fw-bold text-dark mb-3">Customer Interaction Lifecycle</h4>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 text-center">
              {['Customer', 'Request', 'Assignment', 'Follow-up', 'Resolution', 'History'].map((step, idx, arr) => (
                <React.Fragment key={step}>
                  <span className="badge bg-white text-dark border px-3 py-2 rounded-3 fw-semibold" style={{ fontSize: '0.78rem', color: '#1a2f6e', borderColor: '#cbd5e1' }}>
                    {step}
                  </span>
                  {idx < arr.length - 1 && <span className="text-muted small">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column — Business Value & CTA */}
        <div className="col-12 col-lg-5 sipl-reveal-rtl">

          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white sipl-border-left-teal">
            <h3 className="h6 fw-bold text-dark mb-3">Business Value</h3>
            <div className="d-flex flex-column gap-2 mb-2">
              {[
                'Centralize customer information in one hub',
                'Improve follow-up visibility across teams',
                'Reduce scattered customer records',
                'Support better sales & service coordination',
                'Build a clearer history for every relationship',
              ].map((val) => (
                <div key={val} className="d-flex align-items-center gap-2">
                  <span className="rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: 16, height: 16, background: '#0d9488', fontSize: '0.6rem', flexShrink: 0 }}>✓</span>
                  <span className="fw-semibold text-dark small">{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="p-4 rounded-4 text-white" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #1a2f6e 100%)' }}>
            <h4 className="h6 fw-bold text-white mb-2">Need a customer management system?</h4>
            <p className="text-light small mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Let's organize your customer relationships and communication workflows.
            </p>
            <Link to="/contact" className="btn btn-light text-dark fw-bold rounded-3 px-4 py-2 small d-inline-block shadow-sm">
              Discuss a CRM Requirement →
            </Link>
          </div>

        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 6 — SOLUTION 04: LMS SOLUTIONS
   ═══════════════════════════════════════════════════════════════ */
const SolutionLMS = () => (
  <section id="sol-lms" className="py-5 bg-light border-top" aria-label="LMS Solutions">
    <div className="container py-4">

      {/* Header */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ltr">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge sipl-bg-orange text-white px-2.5 py-1 rounded fw-bold" style={{ fontSize: '0.75rem' }}>
              04 / LMS
            </span>
          </div>
          <h2 className="display-6 fw-bold text-dark mb-3">
            Learning Management Solutions
          </h2>
          <p className="fw-bold sipl-text-orange fs-5 mb-3">
            Manage learning, training, and progress in one digital environment.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem', maxWidth: 740 }}>
            LMS solutions provide organizations with a structured platform to manage learning content, users, training programs, assessments, and progress.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-start">

        {/* Left Column — Who it is for & CTA */}
        <div className="col-12 col-lg-5 sipl-reveal-ltr">

          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white sipl-border-left-orange">
            <h3 className="h6 fw-bold text-dark mb-3">Who LMS is suitable for</h3>
            <div className="d-flex flex-column gap-2 mb-3">
              {[
                'Training organizations delivering structured courses',
                'Educational institutions managing digital learning',
                'Corporate learning & development teams',
                'Businesses conducting internal onboarding & compliance',
              ].map((fit) => (
                <div key={fit} className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>→</span>
                  <span className="text-muted small mb-0">{fit}</span>
                </div>
              ))}
            </div>

            <p className="text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
              Core Benefits
            </p>
            <div className="d-flex flex-column gap-1.5">
              {[
                'Centralize learning content & media',
                'Track learner progress in real-time',
                'Organize corporate training programs',
                'Simplify course administration',
                'Improve completion reporting',
              ].map((val) => (
                <div key={val} className="d-flex align-items-center gap-2">
                  <span className="rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: 16, height: 16, background: '#f97316', fontSize: '0.6rem', flexShrink: 0 }}>✓</span>
                  <span className="fw-semibold text-dark small">{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="p-4 rounded-4 text-white" style={{ background: 'linear-gradient(135deg, #f97316 0%, #1a2f6e 100%)' }}>
            <h4 className="h6 fw-bold text-white mb-2">Building a learning or training platform?</h4>
            <p className="text-light small mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Tell us about your content, users, and training objectives.
            </p>
            <Link to="/contact" className="btn btn-light text-dark fw-bold rounded-3 px-4 py-2 small d-inline-block shadow-sm">
              Discuss an LMS Requirement →
            </Link>
          </div>

        </div>

        {/* Right Column — Areas & Features */}
        <div className="col-12 col-lg-7 sipl-reveal-rtl">
          <h3 className="h6 text-uppercase fw-bold text-muted mb-4" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            LMS Platform Capabilities
          </h3>
          <div className="row g-3">
            {[
              { title: 'Course Management', desc: 'Structure courses, modules, lessons, video content, and resources.' },
              { title: 'User & Student Management', desc: 'Enroll users, assign courses, and manage student groups.' },
              { title: 'Assessments & Quizzes', desc: 'Create quizzes, assignments, automated grading, and evaluations.' },
              { title: 'Progress Tracking', desc: 'Monitor individual and cohort progress, completion status, and time spent.' },
              { title: 'Certificates & Milestones', desc: 'Issue digital certificates upon successful course completion.' },
              { title: 'Administrative Dashboards', desc: 'Comprehensive reporting on active learners, course activity, and pass rates.' },
            ].map((lms) => (
              <div key={lms.title} className="col-12 col-md-6">
                <div className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white sipl-card-hover sipl-border-left-orange">
                  <h4 className="h6 fw-bold text-dark mb-1">{lms.title}</h4>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{lms.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 7 — SOLUTION 05: CUSTOM BUSINESS APPLICATIONS
   ═══════════════════════════════════════════════════════════════ */
const SolutionCustom = () => (
  <section id="sol-custom" className="py-5 bg-white border-top" aria-label="Custom Business Applications">
    <div className="container py-4">

      {/* Header */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ltr">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge sipl-bg-navy text-white px-2.5 py-1 rounded fw-bold" style={{ fontSize: '0.75rem' }}>
              05 / CUSTOM SOFTWARE
            </span>
          </div>
          <h2 className="display-6 fw-bold text-dark mb-3">
            Custom Business Applications
          </h2>
          <p className="fw-bold sipl-text-navy fs-5 mb-3">
            When standard software does not fit your workflow.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.95rem', maxWidth: 740 }}>
            Every business has processes that do not fit neatly into an off-the-shelf product. We build custom applications around those workflows, users, roles, data, and operational requirements.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-start">

        {/* Left Column — Applications & Fit Scenarios */}
        <div className="col-12 col-lg-7 sipl-reveal-ltr">
          <h3 className="h6 text-uppercase fw-bold text-muted mb-4" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            Types of Custom Applications We Build
          </h3>
          <div className="row g-3 mb-4">
            {[
              { title: 'Internal Business Portals', desc: 'Secure operational web hubs for staff, teams, and field workers.' },
              { title: 'Workflow & Approval Applications', desc: 'Custom tools that streamline multi-stage approvals and task handoffs.' },
              { title: 'Customer & Vendor Portals', desc: 'Dedicated client hubs for order status, support, and document sharing.' },
              { title: 'Operational Dashboards', desc: 'Consolidate operational metrics from multiple systems into unified views.' },
              { title: 'Document & Archive Systems', desc: 'Organize files, approval histories, and document access control.' },
              { title: 'System Integration Platforms', desc: 'Connect legacy tools and modern APIs to keep data in sync.' },
            ].map((app) => (
              <div key={app.title} className="col-12 col-md-6">
                <div className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white sipl-card-hover sipl-border-left-navy">
                  <h4 className="h6 fw-bold text-dark mb-1">{app.title}</h4>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{app.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column — Good Fit Scenarios & CTA */}
        <div className="col-12 col-lg-5 sipl-reveal-rtl">

          <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-light">
            <h3 className="h6 fw-bold text-dark mb-3">When custom software makes sense</h3>
            <div className="d-flex flex-column gap-2 mb-2">
              {[
                'Existing software does not match your workflow',
                'Multiple disconnected tools need to be unified',
                'Manual processes consume significant team time',
                'A specialized internal tool or portal is required',
                'Legacy software needs modernization',
              ].map((fit) => (
                <div key={fit} className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>→</span>
                  <span className="text-muted small mb-0">{fit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="p-4 rounded-4 text-white" style={{ background: 'linear-gradient(135deg, #1a2f6e 0%, #0d9488 100%)' }}>
            <h4 className="h6 fw-bold text-white mb-2">Have a unique operational process?</h4>
            <p className="text-light small mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Tell us what you want to build and we'll design a custom business application.
            </p>
            <Link to="/contact" className="btn btn-light text-dark fw-bold rounded-3 px-4 py-2 small d-inline-block shadow-sm">
              Discuss a Custom Application →
            </Link>
          </div>

        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 8 — SOLUTION INTEGRATION & SYSTEM ARCHITECTURE
   ═══════════════════════════════════════════════════════════════ */
const SolutionIntegration = () => (
  <section className="py-5 bg-light border-top" aria-label="Connected Solutions and System Integration">
    <div className="container py-4">

      <div className="row mb-5">
        <div className="col-12 col-lg-7 sipl-reveal-ltr">
          <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
            CONNECTED SOLUTIONS
          </span>
          <h2 className="display-6 fw-bold text-dark mb-3">
            Business systems should work together.
          </h2>
          <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '0.95rem' }}>
            Organizations rarely operate with one application. A business solution may need to connect with existing software, APIs, databases, payment systems, communication tools, or reporting platforms.
          </p>
        </div>
      </div>

      <div className="row g-5 align-items-center">

        {/* Left Column — Clean Architecture Diagram */}
        <div className="col-12 col-lg-7 sipl-reveal-ltr">
          <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
            <h3 className="h6 fw-bold text-dark mb-4 text-center">Connected Solution Architecture</h3>
            <div className="d-flex flex-column align-items-center gap-2">
              
              {/* Users layer */}
              <div className="p-2.5 rounded-3 fw-bold small text-white text-center w-75" style={{ background: '#1a2f6e' }}>
                USERS & TEAMS (Role-Based Access)
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: 1 }}>↓</div>

              {/* Interface layer */}
              <div className="p-2.5 rounded-3 fw-bold small text-dark text-center w-75" style={{ background: '#f8fafc', border: '1px solid #cbd5e1' }}>
                WEB / MOBILE APPLICATION LAYER
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: 1 }}>↓</div>

              {/* API Backend */}
              <div className="p-2.5 rounded-3 fw-bold small text-white text-center w-75" style={{ background: '#0d9488' }}>
                API & BACKEND SERVICES
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: 1 }}>↓</div>

              {/* Modules layer */}
              <div className="row g-2 w-100 text-center">
                <div className="col-4">
                  <div className="p-2 rounded-2 fw-semibold small" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.78rem' }}>ERP</div>
                </div>
                <div className="col-4">
                  <div className="p-2 rounded-2 fw-semibold small" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.78rem' }}>CRM</div>
                </div>
                <div className="col-4">
                  <div className="p-2 rounded-2 fw-semibold small" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.78rem' }}>HRM</div>
                </div>
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: 1 }}>↓</div>

              {/* Database & Reporting */}
              <div className="p-2.5 rounded-3 fw-bold small text-white text-center w-75" style={{ background: '#f97316' }}>
                DATABASE & REPORTING ENGINE
              </div>

            </div>
          </div>
        </div>

        {/* Right Column — Integration Capabilities */}
        <div className="col-12 col-lg-5 sipl-reveal-rtl">
          <h3 className="h6 text-uppercase fw-bold text-muted mb-3" style={{ letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            Integration Capabilities
          </h3>
          <div className="d-flex flex-column gap-2 mb-4">
            {[
              { title: 'API Integration', desc: 'Connect modern REST/GraphQL web services.' },
              { title: 'Data Exchange', desc: 'Automate data sync between legacy and new tools.' },
              { title: 'Authentication', desc: 'Role-based access control and Single Sign-On (SSO).' },
              { title: 'Notifications', desc: 'Email, SMS, and system notifications.' },
              { title: 'Reporting Engines', desc: 'Unified analytics and export capabilities.' },
            ].map((item) => (
              <div key={item.title} className="card border-0 shadow-sm rounded-3 p-3 bg-white sipl-card-hover">
                <h4 className="h6 fw-bold text-dark mb-0">{item.title}</h4>
                <p className="text-muted small mb-0" style={{ fontSize: '0.82rem', marginTop: 2 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 9 — HOW WE BUILD BUSINESS SOLUTIONS (Process)
   ═══════════════════════════════════════════════════════════════ */
const SolutionApproach = () => (
  <section className="py-5 bg-white border-top" aria-label="Our Solution Approach">
    <div className="container py-4">

      <div className="row mb-5">
        <div className="col-12 col-lg-7 sipl-reveal-ltr">
          <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
            OUR APPROACH
          </span>
          <h2 className="display-6 fw-bold text-dark mb-3">
            Built around the way your business actually works.
          </h2>
          <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '0.95rem' }}>
            Our methodology ensures business applications fit your operational workflows, user roles, and reporting needs.
          </p>
        </div>
      </div>

      <div className="row g-4">
        {APPROACH_STEPS.map((step, i) => {
          const revealDirs = ['sipl-reveal-ltr', 'sipl-reveal-ttb', 'sipl-reveal-btt', 'sipl-reveal-ttb', 'sipl-reveal-rtl'];
          return (
            <div
              key={step.num}
              className={`col-12 col-sm-6 col-lg ${revealDirs[i]}`}
              style={{ transitionDelay: `${i * 0.14}s` }}
            >
              <div
                className="sipl-process-card p-4 h-100"
                style={{
                  '--step-accent': step.accentColor,
                  background: '#ffffff',
                  borderRadius: '18px',
                  border: '1px solid #e2e8f0',
                  borderTop: `4px solid ${step.accentColor}`,
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="sipl-process-badge fw-bold" style={{ backgroundColor: step.accentColor }}>
                    {step.num}
                  </span>
                  {i < APPROACH_STEPS.length - 1 && (
                    <span className="sipl-process-arrow d-none d-lg-block">→</span>
                  )}
                </div>
                <h3 className="sipl-process-title">{step.title}</h3>
                <p className="sipl-process-desc">{step.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   SECTION 10 — SOLUTION SELECTION GUIDE
   ═══════════════════════════════════════════════════════════════ */
const SolutionSelector = () => {
  const handleScrollTo = (targetId) => {
    navigateWithLoader(targetId);
  };

  return (
    <section className="py-5 bg-light border-top" aria-label="Solution Selection Guide">
      <div className="container py-4">

        <div className="row mb-5">
          <div className="col-12 col-lg-7 sipl-reveal-ltr">
            <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
              WHICH SOLUTION?
            </span>
            <h2 className="display-6 fw-bold text-dark mb-3">
              Start with the business problem.
            </h2>
            <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '0.95rem' }}>
              Identify your primary operational challenge below to jump directly to the matching business solution.
            </p>
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          {SELECTOR_ITEMS.map((item, i) => (
            <div
              key={item.problem}
              onClick={() => handleScrollTo(item.targetId)}
              className="sipl-reveal-btt"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div
                className={`card border-0 shadow-sm rounded-4 p-4 sipl-card-hover ${item.borderClass}`}
                style={{ cursor: 'pointer', background: '#ffffff' }}
              >
                <div className="row align-items-center g-3">
                  <div className="col-12 col-md-6">
                    <span className="text-muted small text-uppercase d-block mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Operational Challenge
                    </span>
                    <h3 className="h6 fw-bold text-dark mb-0">{item.problem}</h3>
                  </div>

                  <div className="col-12 col-md-5">
                    <span className="text-muted small text-uppercase d-block mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Recommended Solution
                    </span>
                    <span className="fw-bold" style={{ color: item.accent, fontSize: '0.95rem' }}>
                      {item.solution}
                    </span>
                  </div>

                  <div className="col-auto text-end d-none d-md-block">
                    <span className="fw-bold" style={{ color: item.accent, fontSize: '1.2rem' }}>
                      ↓
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SECTION 11 — FINAL CTA
   ═══════════════════════════════════════════════════════════════ */
const SolutionsCTA = () => (
  <section
    className="py-5 bg-white text-center position-relative border-top"
    aria-label="Build the Right System"
  >
    {/* 4-color top bar */}
    <div className="sipl-color-bar position-absolute top-0 start-0" />
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 sipl-reveal-ttb">

          <span
            className="text-uppercase fw-bold sipl-text-navy small d-block mb-3"
            style={{ letterSpacing: '0.12em' }}
          >
            BUILD THE RIGHT SYSTEM
          </span>

          <h2 className="display-5 fw-bold text-dark mb-3">
            Have a business workflow that needs a better solution?
          </h2>

          <p
            className="text-muted mb-4"
            style={{ lineHeight: '1.8', maxWidth: 620, margin: '0 auto 1.8rem auto', fontSize: '0.95rem' }}
          >
            Tell us how your business works, where the current process becomes difficult, and what you want to improve. We'll help identify the right approach.
          </p>

          <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3">
            <CtaButton to="/contact" variant="primary">
              Discuss Your Requirement
            </CtaButton>
            <CtaButton to="/services" variant="secondary">
              Explore Services
            </CtaButton>
          </div>

        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN EXPORT
   ═══════════════════════════════════════════════════════════════ */
const Solutions = () => {
  /* SEO — page title */
  useEffect(() => {
    const prev = document.title;
    document.title = 'Solutions | Seikural Innovation Pvt. Ltd.';
    return () => { document.title = prev; };
  }, []);

  /* Scroll reveal — same hook as Home, About, Services & Industries */
  useScrollReveal();

  return (
    <>
      {/* 1. Solutions Hero */}
      <SolutionsHero />

      {/* 2. Solutions Overview & Index Navigation */}
      <SolutionsOverview />

      {/* 3. Solution 01: ERP Solutions */}
      <SolutionERP />

      {/* 4. Solution 02: HRM Solutions */}
      <SolutionHRM />

      {/* 5. Solution 03: CRM Solutions */}
      <SolutionCRM />

      {/* 6. Solution 04: LMS Solutions */}
      <SolutionLMS />

      {/* 7. Solution 05: Custom Business Applications */}
      <SolutionCustom />

      {/* 8. Solution Integration & Architecture */}
      <SolutionIntegration />

      {/* 9. How We Build Business Solutions */}
      <SolutionApproach />

      {/* 10. Solution Selection Guide */}
      <SolutionSelector />

      {/* 11. Final CTA */}
      <SolutionsCTA />
    </>
  );
};

export default Solutions;
